<template>
  <div class="docList">
    <div class="docList__wrap">
      <table class="docTable" role="table">
        <thead class="docTable__head" role="rowgroup">
          <tr role="row">
            <th class="docTable__cell docTable__cell--name" scope="col" role="columnheader">
              {{ $t("documents.table.document") }}
            </th>
            <th class="docTable__cell docTable__cell--category" scope="col" role="columnheader">
              {{ $t("documents.table.category") }}
            </th>
            <th class="docTable__cell docTable__cell--published" scope="col" role="columnheader">
              {{ $t("documents.table.published") }}
            </th>
            <th class="docTable__cell docTable__cell--status" scope="col" role="columnheader">
              {{ $t("documents.table.status") }}
            </th>
            <th class="docTable__cell docTable__cell--action" scope="col" role="columnheader">
              <span class="visually-hidden">{{ $t("documents.actions.download") }}</span>
            </th>
          </tr>
        </thead>
        <tbody role="rowgroup">
          <tr v-for="item in list" :key="item.id" class="docTable__row" role="row">
            <td class="docTable__cell docTable__cell--name" role="cell" :data-label="$t('documents.table.document')">
              {{ item.name }}
            </td>
            <td class="docTable__cell docTable__cell--category" role="cell" :data-label="$t('documents.table.category')">
              {{ item.category?.name }}
            </td>
            <td class="docTable__cell docTable__cell--published" role="cell" :data-label="$t('documents.table.published')">
              {{ formatDate(item.creation_date) }}
            </td>
            <td class="docTable__cell docTable__cell--status" role="cell" :data-label="$t('documents.table.status')">
              <ColoredLabels
                :label="statusLabel(item)"
                :color="item.read ? 'gray' : 'acqua'"
                text-color="white"
                :class="{ 'badge-downloaded': item.read }"
              />
            </td>
            <td class="docTable__cell docTable__cell--action" role="cell">
              <ActionButton
                custom-class="cdlButton docTable__download"
                :title="$t('documents.actions.download')"
                :loading="downloadingId === item.id"
                @click="$emit('download', item)"
              >
                {{ $t("documents.actions.download") }}
                <span class="visually-hidden"> {{ item.name }}</span>
              </ActionButton>
            </td>
          </tr>
          <tr v-if="viewLoader && !list.length && !loadError" class="docTable__row" role="row">
            <td class="docTable__empty" colspan="5" role="cell">
              <InlineError :message="noDataMessage" />
            </td>
          </tr>
          <!-- errore di caricamento: al posto di "nessun documento", resta fino al prossimo caricamento -->
          <tr v-if="viewLoader && loadError" class="docTable__row" role="row">
            <td class="docTable__empty" colspan="5" role="cell">
              <InlineError :message="errorMessage" />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
  <InlineError v-if="viewError" :message="$t('homePage.documentsBox.error')" />
  <Paginator
    v-if="totalPages > 1"
    :total-page="totalPages"
    :current-page="currentPage"
    @change-page="(p) => $emit('change-page', Number(p))"
    @prev-page="currentPage > 1 && $emit('change-page', currentPage - 1)"
    @next-page="
      currentPage < totalPages && $emit('change-page', currentPage + 1)
    "
  />
</template>

<script setup>
import { useI18n } from "vue-i18n";
import ColoredLabels from "@/components/Primitives/Labels/ColoredLabels.vue";
import moment from "moment";
import InlineError from "@/components/Primitives/Feedback/InlineError.vue";
import Paginator from "@/components/Paginator.vue";
import ActionButton from "@/components/Primitives/Buttons/ActionButton.vue";
import "@/assets/scss/TicketsList.scss";

const { t } = useI18n();

defineProps({
  list: {
    type: Array,
    required: true,
  },
  viewLoader: {
    type: Boolean,
    default: true,
  },
  viewError: {
    type: Boolean,
    default: false,
  },
  // errore nel caricamento della lista: mostra errorMessage in tabella al posto del messaggio vuoto
  loadError: {
    type: Boolean,
    default: false,
  },
  errorMessage: {
    type: String,
    default: "",
  },
  noDataMessage: {
    type: String,
    required: true,
  },
  currentPage: {
    type: Number,
    default: 1,
  },
  totalPages: {
    type: Number,
    default: 1,
  },
  downloadingId: {
    type: [Number, String],
    default: null,
  },
});

defineEmits(["download", "change-page"]);

function formatDate(date) {
  return moment(date, "YYYY-MM-DD").format("DD/MM/YYYY");
}

function statusLabel(item) {
  return item.read
    ? t("documents.status.downloaded")
    : t("documents.status.toDownload");
}
</script>
